/* eslint-disable react/jsx-max-props-per-line */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ReplayIcon from '@mui/icons-material/Replay';
import BaseService from '../../../../services/BaseService';
import TableService from '../../../../services/TableService';
import { EmptyState } from '../../../../components/v2';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { withRouter } from 'react-router-dom';
import { SYNC_STATUS } from '../../Integracoes/constants';

const ErrosIntegracaoQuickDrawer = ({
  open,
  onClose,
  apiFilter,
  onReload,
  history,
}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [retryId, setRetryId] = useState(null);

  const fetchErros = useCallback(async () => {
    setLoading(true);
    try {
      const response = await TableService.request('reports/digitacao/me/erros-integracao', {
        ...apiFilter,
        status: SYNC_STATUS.ERROR_SYNC,
      });
      setRows(response?.data?.content || response?.data || []);
    } catch {
      try {
        const fallback = await TableService.request('integrate-entities/filter', {
          ...apiFilter,
          status: SYNC_STATUS.ERROR_SYNC,
          page: 0,
          size: 50,
        });
        setRows(fallback?.data?.content || []);
      } catch {
        setRows([]);
      }
    } finally {
      setLoading(false);
    }
  }, [apiFilter, open]);

  useEffect(() => {
    if (open) fetchErros();
  }, [open, fetchErros]);

  const onRetry = async (row) => {
    if (!row?.integrateEntityId && !row?.id) return;
    const entityId = row.integrateEntityId || row.id;
    setRetryId(entityId);
    try {
      await BaseService.post({ url: `integrate-entities/${entityId}/reprocessar` });
      onReload?.();
      fetchErros();
    } catch (error) {
      console.error(error);
    } finally {
      setRetryId(null);
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: { xs: '100vw', sm: 420 }, p: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h6">Erros de integração</Typography>
          <IconButton onClick={onClose} aria-label="Fechar">
            <CloseIcon />
          </IconButton>
        </Stack>

        {loading ? (
          <Typography variant="body2" color="text.secondary">Carregando…</Typography>
        ) : !rows.length ? (
          <EmptyState
            icon={<ErrorOutlineIcon />}
            title="Nenhum erro"
            message="Seus envios estão integrados corretamente no período."
          />
        ) : (
          <Stack spacing={2}>
            {rows.map((row) => (
              <Box
                key={row.id || row.integrateEntityId}
                sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}
              >
                <Typography variant="subtitle2">
                  NSIR {row.nsirCodigo || row.codigoNsir || '—'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {row.mensagemErro || row.erro || 'Erro na integração'}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      if (row.nsirId) history.push(`/v2/nsir/${row.nsirId}`);
                    }}
                  >
                    Abrir NSIR
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<ReplayIcon />}
                    disabled={retryId === (row.integrateEntityId || row.id)}
                    onClick={() => onRetry(row)}
                  >
                    Reenviar
                  </Button>
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </Box>
    </Drawer>
  );
};

ErrosIntegracaoQuickDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  apiFilter: PropTypes.object.isRequired,
  onReload: PropTypes.func,
  history: PropTypes.object.isRequired,
};

ErrosIntegracaoQuickDrawer.defaultProps = {
  onReload: undefined,
};

export default withRouter(ErrosIntegracaoQuickDrawer);
